import type {
  TFooterNavigationLink,
  TFooterNavigationSection,
} from "@/ui/components/navigation/Footer";
import type { TFunctionOf } from "apps/website/types/i18n";
import { routes } from "apps/website/utils/routes";

export type TFooterNavigationTranslator = TFunctionOf<"Components.Footer.Navigation">;

const articleHref = (
  slug: string,
) => routes.website.articles.show({ params: { slug } });

// ---------------------------------------------------------------------------
// Link IDs
// ---------------------------------------------------------------------------

/** Section IDs used for conditional logic (locale overrides). */
export const SECTION_IDS = {
  tryFresh: "try-fresh",
  aboutKatkin: "about-katkin",
  articles: "articles",
  help: "help",
} as const;

/** Link IDs used for conditional logic (signed-in overrides, locale filtering). */
export const LINK_IDS = {
  myAccount: "help-my-account",
  cancelContracts: "help-cancel-contracts",
  sitemap: "sitemap",
  cookieSettings: "cookie-settings",
} as const;

/** Primary link IDs removed from the German footer. */
export const OMITTED_GERMAN_PRIMARY_LINK_IDS = new Set([
  "try-fresh-reviews",
  "about-katkin-careers",
]);

// ---------------------------------------------------------------------------
// Germany-only URLs
// ---------------------------------------------------------------------------

export const GERMAN_ARTICLE_HREFS = {
  katkinVsDry: "/dry-cat-food",
  katkinVsWet: "/wet-cat-food",
  katkinVsRaw: "/raw-cat-food",
  kitten: "/kitten-food",
} as const;

export const GERMAN_GUEST_CANCELLATION_HREF = "/guest-cancellation";
export const GERMAN_LOGGED_IN_CANCELLATION_HREF = "/customer/my-plans/cancel";
export const GERMAN_WITHDRAWAL_HREF = "/withdrawal";
export const GERMAN_IMPRESSUM_HREF = "/impressum";

/** Maps link IDs to their signed-in href overrides. */
export const SIGNED_IN_HREF_BY_LINK_ID: Record<string, string> = {
  [LINK_IDS.myAccount]: routes.website.customer(),
  [LINK_IDS.cancelContracts]: GERMAN_LOGGED_IN_CANCELLATION_HREF,
};

// ---------------------------------------------------------------------------
// Base link data (shared across locales)
// ---------------------------------------------------------------------------

const getTryFreshSection = (
  t: TFooterNavigationTranslator,
): TFooterNavigationSection => ({
  id: SECTION_IDS.tryFresh,
  title: t("Primary.TryFresh.title"),
  links: [
    {
      id: "try-fresh-start-my-trial",
      label: t("Primary.TryFresh.startMyTrial"),
      href: routes.website.signUp.fresh(),
    },
    {
      id: "try-fresh-why-fresh",
      label: t("Primary.TryFresh.whyFresh"),
      href: routes.website.whyFresh(),
    },
    {
      id: "try-fresh-refer-a-friend",
      label: t("Primary.TryFresh.referAFriend"),
      href: routes.website.referAFriend({
        config: { query: { situation: "web_footer" } },
      }),
    },
    {
      id: "try-fresh-reviews",
      label: t("Primary.TryFresh.reviews"),
      href: routes.thirdParty.reviews(),
    },
  ],
});

const getAboutKatKinSection = (
  t: TFooterNavigationTranslator,
): TFooterNavigationSection => ({
  id: SECTION_IDS.aboutKatkin,
  title: t("Primary.AboutKatKin.title"),
  links: [
    {
      id: "about-katkin-about-us",
      label: t("Primary.AboutKatKin.aboutUs"),
      href: routes.website.aboutUs(),
    },
    {
      id: "about-katkin-products",
      label: t("Primary.AboutKatKin.ourProducts"),
      href: routes.website.products(),
    },
    {
      id: "about-katkin-blog",
      label: t("Primary.AboutKatKin.blog"),
      href: routes.website.articles.index(),
    },
    {
      id: "about-katkin-careers",
      label: t("Primary.AboutKatKin.careers"),
      href: routes.thirdParty.careers(),
    },
  ],
});

const getArticlesSection = (
  t: TFooterNavigationTranslator,
): TFooterNavigationSection => ({
  id: SECTION_IDS.articles,
  title: t("Primary.Articles.title"),
  links: [
    {
      id: "articles-secret-lives-of-cats",
      label: t("Primary.Articles.secretLivesOfCats"),
      href: articleHref(
        "the-secret-lives-of-cats-weird-and-wonderful-breed-facts",
      ),
    },
    {
      id: "articles-science-behind-fresh-cat-food",
      label: t("Primary.Articles.scienceBehindFreshCatFood"),
      href: articleHref("the-science-behind-fresh-food"),
    },
    {
      id: "articles-whats-wrong-with-cat-food",
      label: t("Primary.Articles.whatsWrongWithCatFood"),
      href: articleHref("what-s-wrong-with-cat-food"),
    },
    {
      id: "articles-understanding-fussy-cats",
      label: t("Primary.Articles.understandingFussyCats"),
      href: articleHref("understanding-fussier-cats"),
    },
  ],
});

const getHelpSection = (
  t: TFooterNavigationTranslator,
): TFooterNavigationSection => ({
  id: SECTION_IDS.help,
  title: t("Primary.Help.title"),
  links: [
    {
      id: LINK_IDS.myAccount,
      label: t("Primary.Help.myAccount"),
      href: routes.website.login(),
    },
    {
      id: "help-faqs",
      label: t("Primary.Help.faqs"),
      href: routes.thirdParty.help(),
    },
    {
      id: "help-contact-us",
      label: t("Primary.Help.contactUs"),
      href: routes.thirdParty.contactUs(),
    },
    {
      id: "help-breeders-club",
      label: t("Primary.Help.breedersClub"),
      href: routes.website.breeder(),
    },
  ],
});

export const getBasePrimarySections = (
  t: TFooterNavigationTranslator,
): TFooterNavigationSection[] => [
  getTryFreshSection(t),
  getAboutKatKinSection(t),
  getArticlesSection(t),
  getHelpSection(t),
];

export const getBaseSecondaryLinks = (
  t: TFooterNavigationTranslator,
): TFooterNavigationLink[] => [
  {
    id: "terms-and-conditions",
    label: t("Secondary.termsAndConditions"),
    href: routes.website.legal.terms(),
  },
  {
    id: "privacy",
    label: t("Secondary.privacy"),
    href: routes.website.legal.privacyPolicy(),
  },
  {
    id: LINK_IDS.sitemap,
    label: t("Secondary.sitemap"),
    href: routes.website.sitemap(),
  },
  {
    id: LINK_IDS.cookieSettings,
    label: t("Secondary.cookieSettings"),
    href: routes.website.legal.privacyPolicy(),
  },
];

export const getSocialLinks = (
  t: TFooterNavigationTranslator,
): TFooterNavigationLink[] => [
  {
    id: "email",
    label: t("Social.email"),
    href: routes.thirdParty.email(),
    icon: "email",
    ariaLabel: t("Social.emailAriaLabel"),
  },
  {
    id: "facebook",
    label: t("Social.facebook"),
    href: routes.thirdParty.clubhouse(),
    icon: "facebook",
  },
  {
    id: "instagram",
    label: t("Social.instagram"),
    href: routes.thirdParty.instagram(),
    icon: "instagram",
  },
  {
    id: "youtube",
    label: t("Social.youtube"),
    href: routes.thirdParty.youtube(),
    icon: "youtube",
  },
];

// ---------------------------------------------------------------------------
// Germany-only link data
// ---------------------------------------------------------------------------

export const getGermanArticleLinks = (
  t: TFooterNavigationTranslator,
): TFooterNavigationLink[] => [
  {
    id: "articles-katkin-vs-dry",
    label: t("Primary.Articles.katkinVsDry"),
    href: GERMAN_ARTICLE_HREFS.katkinVsDry,
  },
  {
    id: "articles-katkin-vs-wet",
    label: t("Primary.Articles.katkinVsWet"),
    href: GERMAN_ARTICLE_HREFS.katkinVsWet,
  },
  {
    id: "articles-katkin-vs-raw",
    label: t("Primary.Articles.katkinVsRaw"),
    href: GERMAN_ARTICLE_HREFS.katkinVsRaw,
  },
  {
    id: "articles-kitten",
    label: t("Primary.Articles.kitten"),
    href: GERMAN_ARTICLE_HREFS.kitten,
  },
];

export const getGermanHelpLinks = (
  t: TFooterNavigationTranslator,
): TFooterNavigationLink[] => [
  {
    id: LINK_IDS.cancelContracts,
    label: t("Primary.Help.cancelContracts"),
    href: GERMAN_GUEST_CANCELLATION_HREF,
  },
  {
    id: "help-withdraw-from-contract",
    label: t("Primary.Help.withdrawFromContract"),
    href: GERMAN_WITHDRAWAL_HREF,
  },
];
